import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Key } from 'lucide-react';
import { resetPassword } from '../api';
import { AuthCard, PasswordField } from '../components/AuthCard';
import useEmailLinkToken from '../hooks/useEmailLinkToken';
import { PASSWORD_HINT, passwordProblem } from '../utils/emailLinks';

function ResetPassword() {
  const { token, status, details, error: linkError, retry } = useEmailLinkToken('reset');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    const problem = passwordProblem(password);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      const res = await resetPassword(token, password);
      setDone(res.data?.message || 'Your password has been reset.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not reset your password. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderBody = () => {
    if (done) {
      return (
        <>
          <div className="alert alert-success" role="status">{done}</div>
          <Link to="/login" className="btn btn-primary login-btn">Sign in</Link>
        </>
      );
    }

    if (status === 'checking') {
      return <div className="loading-container" aria-label="Checking your link"><div className="spinner"></div></div>;
    }

    if (status === 'failed') {
      return (
        <>
          <div className="alert alert-error" role="alert">{linkError}</div>
          <button type="button" className="btn btn-primary login-btn" onClick={retry}>Try again</button>
        </>
      );
    }

    if (status === 'invalid') {
      return (
        <>
          <div className="alert alert-error" role="alert">{linkError}</div>
          <p className="login-text">Reset links work once and expire after a while.</p>
          <Link to="/forgot-password" className="btn btn-primary login-btn">Request a new link</Link>
        </>
      );
    }

    return (
      <>
        {details?.email && <p className="login-text">Choose a new password for <strong>{details.email}</strong>.</p>}
        {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}
        <form onSubmit={handleSubmit} noValidate>
          {/* Lets a password manager file the new password under the right account. */}
          {details?.email && <input type="email" value={details.email} autoComplete="username" readOnly hidden />}
          <PasswordField id="reset-password" label="New password" value={password} onChange={setPassword} placeholder="Enter new password" describedBy="reset-password-hint" />
          <p id="reset-password-hint" className="login-hint">{PASSWORD_HINT}</p>
          <PasswordField id="reset-password-confirm" label="Confirm password" value={confirmPassword} onChange={setConfirmPassword} placeholder="Confirm new password" />
          <button type="submit" className="btn btn-primary login-btn" disabled={saving}>
            {saving ? <span>Saving...</span> : (<><Key size={18} /><span>Reset password</span></>)}
          </button>
        </form>
      </>
    );
  };

  return (
    <AuthCard subtitle="Choose a new password">
      {renderBody()}
      {!done && <p className="login-request"><Link to="/login">Back to sign in</Link></p>}
    </AuthCard>
  );
}

export default ResetPassword;
